// src/store/contactSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/lib/api';

// ── Thunks ─────────────────────────────────────────────────────────────

// Получить список организаций/контактов
export const getContacts = createAsyncThunk(
  'contacts/getContacts',
  async (_, { rejectWithValue }) => {
    try {
      // api уже содержит baseURL и токены
      const { data } = await api.get('/api/contacts/');
      // Бэкенд может вернуть либо {results: [...]}, либо просто [...]
      return Array.isArray(data) ? data : (data.results ?? []);
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Network Error');
    }
  }
);

// Загрузить CSV/Excel с контактами
export const uploadContacts = createAsyncThunk(
  'contacts/uploadContacts',
  async (formData, { rejectWithValue }) => {
    try {
      // ВАЖНО: не задавать вручную Content-Type — axios сам поставит boundary
      const { data } = await api.post('/api/contacts/upload/', formData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Upload failed');
    }
  }
);

// (опционально) Создать организацию
export const createOrganization = createAsyncThunk(
  'contacts/createOrganization',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/api/contacts/organizations/', payload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Create failed');
    }
  }
);

// (опционально) Обновить организацию
export const updateOrganizationRequest = createAsyncThunk(
  'contacts/updateOrganizationRequest',
  async ({ orgId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/api/contacts/organizations/${orgId}/`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Update failed');
    }
  }
);

// (опционально) Удалить организацию
export const deleteOrganizationRequest = createAsyncThunk(
  'contacts/deleteOrganizationRequest',
  async (orgId, { rejectWithValue }) => {
    try {
      await api.delete(`/api/contacts/organizations/${orgId}/`);
      return orgId;
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Delete failed');
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────

const contactSlice = createSlice({
  name: 'contacts',
  initialState: {
    organizations: [],
    status: 'idle', // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {
    // локальное обновление уже существующей организации в сторе
    updateOrganizationLocally(state, action) {
      const updatedOrg = action.payload;
      const idx = state.organizations.findIndex((o) => o.id === updatedOrg.id);
      if (idx !== -1) state.organizations[idx] = updatedOrg;
    },
  },
  extraReducers: (builder) => {
    builder
      // getContacts
      .addCase(getContacts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.organizations = action.payload || [];
      })
      .addCase(getContacts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch contacts';
      })

      // uploadContacts
      .addCase(uploadContacts.pending, (state) => {
        state.error = null;
      })
      .addCase(uploadContacts.fulfilled, (state, action) => {
        // если бэкенд возвращает новые/обновлённые записи — можно слить
        // здесь просто обновим список, если прислали organizations
        if (Array.isArray(action.payload)) {
          state.organizations = action.payload;
        } else if (Array.isArray(action.payload?.organizations)) {
          state.organizations = action.payload.organizations;
        }
      })
      .addCase(uploadContacts.rejected, (state, action) => {
        state.error = action.payload || 'Upload failed';
      })

      // createOrganization
      .addCase(createOrganization.fulfilled, (state, action) => {
        if (action.payload) state.organizations.unshift(action.payload);
      })

      // updateOrganizationRequest
      .addCase(updateOrganizationRequest.fulfilled, (state, action) => {
        const u = action.payload;
        const idx = state.organizations.findIndex((o) => o.id === u.id);
        if (idx !== -1) state.organizations[idx] = u;
      })

      // deleteOrganizationRequest
      .addCase(deleteOrganizationRequest.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.organizations = state.organizations.filter((o) => o.id !== deletedId);
      });
  },
});

export const { updateOrganizationLocally } = contactSlice.actions;
export default contactSlice.reducer;